import {Empty, Tag, Timeline, Typography} from 'antd';
import type {
  DailyExerciseHistoryEntry,
  DailyExerciseHistoryChange,
} from '@PikaDoApp/npm-pikado-logic';

const {Text} = Typography;

interface DailyExerciseHistoryProps {
  history?: DailyExerciseHistoryEntry[];
}

const ACTION_COLORS: Record<string, string> = {
  create: 'blue',
  update: 'gold',
  approve: 'green',
  reject: 'red',
};

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return isNaN(date.getTime()) ? value : date.toLocaleString();
};

const renderChange = (change: DailyExerciseHistoryChange) => {
  const from = change.from ?? '∅';
  const to = change.to ?? '∅';
  return (
    <div key={change.field} style={{fontSize: 12, marginTop: 2}}>
      <Text strong>{change.field}</Text>: <Text delete>{from}</Text> →{' '}
      <Text type="success">{to}</Text>
    </div>
  );
};

/**
 * Renders a daily exercise's audit trail as a timeline: who did what and when,
 * with a per-field diff for edits and the reason for rejections.
 */
export const DailyExerciseHistory = ({history}: DailyExerciseHistoryProps) => {
  if (!history || history.length === 0) {
    return <Empty description="No history recorded" />;
  }

  // Most recent first.
  const ordered = [...history].sort((a, b) =>
    (b.changedAt || '').localeCompare(a.changedAt || ''),
  );

  return (
    <Timeline
      style={{marginTop: 8}}
      items={ordered.map(entry => ({
        color: ACTION_COLORS[entry.action] ?? 'gray',
        children: (
          <div>
            <div>
              <Tag color={ACTION_COLORS[entry.action] ?? 'default'}>
                {String(entry.action).toUpperCase()}
              </Tag>
              <Text type="secondary" style={{fontSize: 12}}>
                {formatDate(entry.changedAt)}
              </Text>
            </div>
            <div style={{fontSize: 13}}>
              by <Text strong>{entry.changedBy || 'unknown'}</Text>
            </div>
            {entry.reason && (
              <div style={{fontSize: 12}}>
                <Text type="secondary">Reason: </Text>
                {entry.reason}
              </div>
            )}
            {entry.changes?.map(renderChange)}
          </div>
        ),
      }))}
    />
  );
};
